import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Icon, type IconName } from '@/components/common/Icon';
import { cn } from '@/lib/utils';

const EDITOR_CONTENT_CLASSES =
  '[&_.ProseMirror]:min-h-[8rem] [&_.ProseMirror]:outline-none [&_.ProseMirror]:text-sm ' +
  '[&_.ProseMirror]:text-zooindigo [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 ' +
  '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_li]:my-1 ' +
  '[&_.ProseMirror_p]:my-2 [&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:font-bold';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  isDisabled: boolean;
  isHidden?: boolean;
  hasError?: boolean;
  ariaLabel: string;
}

export function RichTextEditor({
  value,
  onChange,
  isDisabled,
  isHidden = false,
  hasError = false,
  ariaLabel,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable: isDisabled === false,
    onUpdate: function handleUpdate({ editor: updatedEditor }) {
      onChange(updatedEditor.getHTML());
    },
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
  });

  return (
    <div
      className={cn(
        'rounded-xl border bg-zoowhite overflow-hidden',
        hasError === true ? 'border-zooerrorborder' : 'border-zooborder',
        isHidden === true ? 'hidden' : ''
      )}
    >
      <RichTextToolbar editor={editor} isDisabled={isDisabled} />
      <EditorContent editor={editor} className={cn('px-3 py-2', EDITOR_CONTENT_CLASSES)} />
    </div>
  );
}

interface RichTextToolbarProps {
  editor: Editor | null;
  isDisabled: boolean;
}

function RichTextToolbar({ editor, isDisabled }: RichTextToolbarProps) {
  const { t } = useTranslation('common');
  if (editor === null) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-zooborder bg-zoooffwhite px-2 py-1.5">
      <ToolbarButton
        iconName="Bold"
        label={t('richText.bold')}
        isActive={editor.isActive('bold')}
        isDisabled={isDisabled}
        onClick={function toggle() {
          editor.chain().focus().toggleBold().run();
        }}
      />
      <ToolbarButton
        iconName="Italic"
        label={t('richText.italic')}
        isActive={editor.isActive('italic')}
        isDisabled={isDisabled}
        onClick={function toggle() {
          editor.chain().focus().toggleItalic().run();
        }}
      />
      <ToolbarButton
        iconName="Heading2"
        label={t('richText.heading')}
        isActive={editor.isActive('heading', { level: 2 })}
        isDisabled={isDisabled}
        onClick={function toggle() {
          editor.chain().focus().toggleHeading({ level: 2 }).run();
        }}
      />
      <ToolbarButton
        iconName="List"
        label={t('richText.bulletList')}
        isActive={editor.isActive('bulletList')}
        isDisabled={isDisabled}
        onClick={function toggle() {
          editor.chain().focus().toggleBulletList().run();
        }}
      />
      <ToolbarButton
        iconName="ListOrdered"
        label={t('richText.orderedList')}
        isActive={editor.isActive('orderedList')}
        isDisabled={isDisabled}
        onClick={function toggle() {
          editor.chain().focus().toggleOrderedList().run();
        }}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  iconName: IconName;
  label: string;
  isActive: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

function ToolbarButton({ iconName, label, isActive, isDisabled, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={isActive}
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-zooprimary',
        isActive === true
          ? 'bg-zooprimary text-zoowhite'
          : 'text-zoomuted hover:bg-zoovpalelavender hover:text-zooprimary'
      )}
    >
      <Icon name={iconName} />
    </button>
  );
}
